/**
 * Environment Configuration
 * Centralized environment-based configuration
 * 
 * IMPORTANT: Runtime config from public/config.json takes precedence.
 * Use getRuntimeConfig() for values that should be changeable without rebuilding.
 * This file provides build-time defaults only.
 */

export type AppEnvironment = 'development' | 'production' | 'test';
export type ApiMode = 'real';

interface EnvironmentConfig {
  env: AppEnvironment;
  apiMode: ApiMode;
  isDevelopment: boolean;
  isProduction: boolean;
  isTest: boolean;
  socket: {
    url: string;
    reconnectionDelay: number;
    reconnectionDelayMax: number;
    reconnectionAttempts: number;
    randomizationFactor: number;
  };
  api: {
    configEndpoint: string;
    requestTimeout: number;
    maxRetries: number;
  };
  session: {
    idleTimeout: number;
    warningDuration: number;
  };
  cognito: {
    region: string;
    userPoolId: string;
    clientId: string;
    idpEndpoint: string;
    authFlow: string;
    userPoolAuthToken: string;
    redirectUri: string;
    logoutUri: string;
    oauthResponseType: string;
  };
  aws: {
    socket: string;
    domain: string;
  };
}

/**
 * Get environment variable with fallback
 */
function getEnvVar(key: string, defaultValue: string): string {
  return (import.meta.env[`VITE_${key}`] as string) || defaultValue;
}

/**
 * Parse integer environment variable
 */
function getEnvInt(key: string, defaultValue: number): number {
  const value = import.meta.env[`VITE_${key}`] as string | undefined;
  return value ? parseInt(value, 10) : defaultValue;
}

/**
 * Parse float environment variable
 */
function getEnvFloat(key: string, defaultValue: number): number {
  const value = import.meta.env[`VITE_${key}`] as string | undefined;
  return value ? parseFloat(value) : defaultValue;
}

/**
 * Initialize environment configuration
 */
function initializeEnvironmentConfig(): EnvironmentConfig {
  const env = (getEnvVar('ENV', 'development') as AppEnvironment) || 'development';
  const apiMode = (getEnvVar('API_MODE', 'real') as ApiMode) || 'real';

  const isDevelopment = env === 'development';
  const isProduction = env === 'production';
  const isTest = env === 'test';

  return {
    env,
    apiMode,
    isDevelopment,
    isProduction,
    isTest,
    socket: {
      url: getEnvVar('SOCKET_URL', 'http://localhost:3001'),
      reconnectionDelay: getEnvInt('SOCKET_RECONNECTION_DELAY', 1000),
      reconnectionDelayMax: getEnvInt('SOCKET_RECONNECTION_DELAY_MAX', 4000),
      reconnectionAttempts: getEnvInt('SOCKET_RECONNECTION_ATTEMPTS', 3),
      randomizationFactor: getEnvFloat('SOCKET_RANDOMIZATION_FACTOR', 0.2),
    },
    api: {
      configEndpoint: getEnvVar('CONFIG_API_ENDPOINT', 'http://localhost:3001/api/config'),
      requestTimeout: 25000,
      maxRetries: 1,
    },
    session: {
      idleTimeout: getEnvInt('IDLE_TIMEOUT', 43200) * 1000,
      warningDuration: getEnvInt('WARNING_DURATION', 5) * 1000,
    },
    cognito: {
      region: 'ap-southeast-2',
      userPoolId: getEnvVar('AWS_COGNITO_USER_POOL_ID', ''),
      clientId: getEnvVar('AWS_COGNITO_CLIENT_ID', ''),
      idpEndpoint: 'https://cognito-idp.ap-southeast-2.amazonaws.com/',
      authFlow: 'USER_PASSWORD_AUTH',
      userPoolAuthToken: getEnvVar('AWS_COGNITO_USER_POOL_AUTH_TOKEN', ''),
      redirectUri: getEnvVar('AWS_COGNITO_REDIRECT_URI', ''),
      logoutUri: getEnvVar('AWS_COGNITO_LOGOUT_URI', ''),
      oauthResponseType: getEnvVar('AWS_COGNITO_OAUTH_RESPONSE_TYPE', 'code'),
    },
    aws: {
      socket: getEnvVar('AWS_SOCKET', ''),
      domain: getEnvVar('DOMAIN', ''),
    },
  };
}

// Create singleton instance
export const environmentConfig = initializeEnvironmentConfig();

/**
 * Convenience functions for checking environment and API mode
 */
export const isUsingRealApi = (): boolean => true;

export default environmentConfig;
